// ============================================================
// SATELLITE IMAGERY — Esri World Imagery tiles
// Free, no API key, building-level detail at zoom 19
// Fetches a 3×3 grid of tiles, stitches into one image,
// returns a data URL + real-world bounds in feet
// ============================================================

const ESRI_TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile';
const TILE_SIZE = 256;
const ZOOM = 19; // building-level detail
const GRID = 3;  // 3×3 tile grid centered on property

export interface ImageryResult {
  dataUrl: string;
  widthFeet: number;
  heightFeet: number;
  // Offset of the property center from the top-left of the image, in feet
  centerOffsetX: number;
  centerOffsetY: number;
}

// --- Coordinate math ---

function latLonToTile(lat: number, lon: number, zoom: number): { x: number; y: number } {
  const n = Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

function tileToLatLon(x: number, y: number, zoom: number): { lat: number; lon: number } {
  const n = Math.pow(2, zoom);
  const lon = (x / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
  const lat = (latRad * 180) / Math.PI;
  return { lat, lon };
}

function latLonToFeetOffset(lat: number, lon: number, centerLat: number, centerLon: number) {
  const metersPerDegLat = 111320;
  const metersPerDegLon = 111320 * Math.cos((centerLat * Math.PI) / 180);
  const dLat = lat - centerLat;
  const dLon = lon - centerLon;
  const yMeters = dLat * metersPerDegLat;
  const xMeters = dLon * metersPerDegLon;
  return { x: xMeters * 3.28084, y: yMeters * 3.28084 };
}

// --- Tile fetching ---

async function fetchTile(x: number, y: number): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const url = `${ESRI_TILE_URL}/${ZOOM}/${y}/${x}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// --- Main: fetch 3×3 grid and stitch ---

export async function fetchSatelliteImagery(lat: number, lon: number): Promise<ImageryResult | null> {
  const { x: tileXf, y: tileYf } = latLonToTile(lat, lon, ZOOM);
  const tileX = Math.floor(tileXf);
  const tileY = Math.floor(tileYf);

  // Top-left tile in the 3×3 grid
  const startTileX = tileX - 1;
  const startTileY = tileY - 1;

  // Fetch all 9 tiles in parallel
  const tilePromises: Promise<HTMLImageElement | null>[] = [];
  for (let dy = 0; dy < GRID; dy++) {
    for (let dx = 0; dx < GRID; dx++) {
      tilePromises.push(fetchTile(startTileX + dx, startTileY + dy));
    }
  }
  const tiles = await Promise.all(tilePromises);

  // If all tiles failed, return null (likely offline)
  if (tiles.every((t) => t === null)) {
    return null;
  }

  // Stitch tiles onto a canvas
  const canvas = document.createElement('canvas');
  canvas.width = TILE_SIZE * GRID;
  canvas.height = TILE_SIZE * GRID;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Fill with dark background for any missing tiles
  ctx.fillStyle = '#1a2a1a';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  for (let dy = 0; dy < GRID; dy++) {
    for (let dx = 0; dx < GRID; dx++) {
      const tile = tiles[dy * GRID + dx];
      if (tile) {
        ctx.drawImage(tile, dx * TILE_SIZE, dy * TILE_SIZE, TILE_SIZE, TILE_SIZE);
      }
    }
  }

  // Calculate the geographic bounds of the stitched image
  const northWest = tileToLatLon(startTileX, startTileY, ZOOM);
  const southEast = tileToLatLon(startTileX + GRID, startTileY + GRID, ZOOM);

  // Convert bounds to feet offsets from the property center
  const nwFeet = latLonToFeetOffset(northWest.lat, northWest.lon, lat, lon);
  const seFeet = latLonToFeetOffset(southEast.lat, southEast.lon, lat, lon);

  const widthFeet = seFeet.x - nwFeet.x;
  const heightFeet = nwFeet.y - seFeet.y; // y is inverted (north is positive)

  // Center offset: where in the image (in feet from top-left) is the property?
  const centerOffsetX = -nwFeet.x; // property is at x=0 in our 3D scene
  const centerOffsetY = nwFeet.y;  // property is at y=0; image y grows downward

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.85),
    widthFeet,
    heightFeet,
    centerOffsetX,
    centerOffsetY,
  };
}
